import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, X, Film } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, imgSrc, uploadImage, uploadVideo, formatApiError } from "../../lib/api";

export default function HeroSettings() {
  const [s, setS] = useState({ hero_video: "", hero_poster: "" });
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/settings").then(({ data }) => setS({ hero_video: "", hero_poster: "", ...data })).catch(() => {});
  }, []);

  const save = async (next) => {
    try {
      const { data } = await api.put("/settings", next);
      setS(data);
      toast.success("Impostazioni salvate");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onFile = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(kind);
    try {
      const { storage_path } = kind === "video" ? await uploadVideo(file) : await uploadImage(file);
      await save({ ...s, [kind === "video" ? "hero_video" : "hero_poster"]: storage_path });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
      e.target.value = "";
    }
  };

  const btn = "inline-flex w-fit cursor-pointer items-center gap-2 border border-[#262420] px-4 py-2 text-xs uppercase tracking-wider text-[#A39E93] transition-colors hover:border-[#C8A86B]/50 hover:text-[#C8A86B]";
  const labelCls = "text-xs font-mono uppercase tracking-wider text-[#827D72]";

  return (
    <div data-testid="hero-settings" className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
      <div className="flex flex-col gap-3 border border-[#1C1A17] bg-[#0F0F0E] p-6">
        <Label className={labelCls}>Video Hero (MP4/WEBM, max 50MB) — in loop, muto</Label>
        {s.hero_video ? (
          <div className="relative">
            <video src={imgSrc(s.hero_video)} muted loop autoPlay playsInline className="aspect-video w-full border border-[#262420] object-cover" />
            <button data-testid="remove-hero-video" onClick={() => save({ ...s, hero_video: "" })} className="absolute -right-2 -top-2 border border-[#262420] bg-[#080808] p-1 text-[#A39E93] hover:text-[#C25D53]"><X size={12} /></button>
          </div>
        ) : (
          <div className="flex aspect-video items-center justify-center border border-dashed border-[#262420] text-[#666157]"><Film size={22} /></div>
        )}
        <label data-testid="upload-hero-video" className={btn}>
          <Upload size={13} /> {busy === "video" ? "Caricamento…" : "Carica video"}
          <input type="file" accept="video/mp4,video/webm,video/quicktime" className="hidden" onChange={(e) => onFile(e, "video")} />
        </label>
        <div className="flex gap-2">
          <Input data-testid="hero-video-url" placeholder="oppure URL video esterno (https://…)" value={s.hero_video?.startsWith("http") ? s.hero_video : ""} onChange={(e) => setS({ ...s, hero_video: e.target.value })} className="border-[#262420] bg-[#080808] text-[#F3EFE6]" />
          <button data-testid="save-hero-video-url" onClick={() => save(s)} className="border border-[#C8A86B] px-4 text-xs uppercase tracking-wider text-[#C8A86B] hover:bg-[#C8A86B] hover:text-[#080808]">Salva</button>
        </div>
      </div>
      <div className="flex flex-col gap-3 border border-[#1C1A17] bg-[#0F0F0E] p-6">
        <Label className={labelCls}>Immagine Hero / Poster (fallback senza video)</Label>
        {s.hero_poster ? (
          <div className="relative">
            <img src={imgSrc(s.hero_poster)} alt="" className="aspect-video w-full border border-[#262420] object-cover" />
            <button data-testid="remove-hero-poster" onClick={() => save({ ...s, hero_poster: "" })} className="absolute -right-2 -top-2 border border-[#262420] bg-[#080808] p-1 text-[#A39E93] hover:text-[#C25D53]"><X size={12} /></button>
          </div>
        ) : (
          <div className="flex aspect-video items-center justify-center border border-dashed border-[#262420] text-xs font-mono text-[#666157]">Default: foto pergola</div>
        )}
        <label data-testid="upload-hero-poster" className={btn}>
          <Upload size={13} /> {busy === "poster" ? "Caricamento…" : "Carica immagine"}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => onFile(e, "poster")} />
        </label>
      </div>
    </div>
  );
}
